import { useMemo } from "react";
import { Box, ListSubheader, MenuItem, TextField } from "@mui/material";
import { rasitusluokkaPresets } from "src/utils/quote/rasitusluokat";
import { labelFor } from "src/utils/quote/quoteConstants";
import { useT } from "src/i18n";

// Labels look like "XC3-4, XF1 — Säänkestävä (pystypinta)". Splitting on the
// em-dash gives us (codes, descriptor) which we can lay out independently —
// descriptor on the left as the primary read, codes on the right as secondary.
function splitLabel(label) {
    const sepIdx = label.indexOf(" — ");
    if (sepIdx === -1) return { codes: null, descriptor: label };
    return { codes: label.slice(0, sepIdx), descriptor: label.slice(sepIdx + 3) };
}

function RichItem({ preset, exampleLabel }) {
    if (!preset.meaning) {
        return <Box sx={{ fontWeight: 500 }}>{preset.label}</Box>;
    }
    const { codes, descriptor } = splitLabel(preset.label);
    return (
        <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 1.5,
                    lineHeight: 1.3,
                }}
            >
                <Box sx={{ fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0 }}>
                    {descriptor}
                </Box>
                {codes && (
                    <Box
                        sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                        }}
                    >
                        {codes}
                    </Box>
                )}
            </Box>
            <Box sx={{ fontSize: 12, color: "text.secondary", mt: 0.5, lineHeight: 1.45 }}>
                {preset.meaning}
            </Box>
            <Box sx={{ fontSize: 11, color: "text.secondary", fontStyle: "italic", mt: 0.25, lineHeight: 1.45, opacity: 0.85 }}>
                {exampleLabel} {preset.example}
            </Box>
        </Box>
    );
}

// Render the preset list as flat MenuItems, with ListSubheader rows separating groups.
function renderPresetMenuItems(presets, exampleLabel) {
    const items = [];
    let lastGroup;
    for (const p of presets) {
        if (p.group !== lastGroup && p.group != null) {
            items.push(
                <ListSubheader
                    key={`hdr-${p.group}`}
                    sx={{
                        lineHeight: 2.2,
                        fontFamily: "'Black Ops One', serif",
                        fontSize: 11,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        bgcolor: "background.paper",
                    }}
                >
                    {p.group}
                </ListSubheader>,
            );
            lastGroup = p.group;
        }
        items.push(
            <MenuItem
                key={p.value || "empty"}
                value={p.value}
                sx={{
                    whiteSpace: "normal",
                    alignItems: "flex-start",
                    py: 1,
                    px: 2,
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    "&:first-of-type": { borderTop: "none" },
                }}
            >
                <RichItem preset={p} exampleLabel={exampleLabel} />
            </MenuItem>,
        );
    }
    return items;
}

export default function RasitusluokkaField({ value, onChange, helperText }) {
    const t = useT();
    const presets = useMemo(() => rasitusluokkaPresets(t), [t]);
    return (
        <TextField
            label={t("wizard.shared.rasitusluokkaLabel")}
            select
            fullWidth
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            helperText={helperText || t("wizard.rasitusluokkaField.helperDefault")}
            slotProps={{
                select: {
                    renderValue: (selected) => (selected ? labelFor(presets, selected, selected) : ""),
                    MenuProps: {
                        slotProps: {
                            paper: {
                                sx: {
                                    // Capped so the menu fits below the field (matches the
                                    // MuiSelect default in themes.js); a taller menu flips up
                                    // over the cursor and MUI's open-on-mousedown ghost-selects.
                                    maxHeight: 400,
                                    width: { xs: "calc(100vw - 32px)", sm: 480 },
                                    maxWidth: "calc(100vw - 32px)",
                                },
                            },
                        },
                    },
                }
            }}
        >
            {renderPresetMenuItems(presets, t("wizard.shared.example"))}
        </TextField>
    );
}
